import { useEffect, useState } from 'react';
import {
  BidStatus,
  FirmType,
  RequestStatus,
  ServiceType,
  Timestamp,
  Urgency,
} from '../../api/marketplace';

export const SERVICE_TYPES: ServiceType[] = [
  'visa',
  'residence_permit',
  'citizenship',
  'bank_account',
  'company_formation',
  'other',
];

export const SERVICE_LABELS: Record<ServiceType, string> = {
  visa: 'Visa',
  residence_permit: 'Residence permit',
  citizenship: 'Citizenship',
  bank_account: 'Bank account',
  company_formation: 'Company formation',
  other: 'Other',
};

export const URGENCIES: Urgency[] = ['low', 'normal', 'high'];

export const URGENCY_LABELS: Record<Urgency, string> = {
  low: 'Low',
  normal: 'Normal',
  high: 'High',
};

export const FIRM_TYPES: FirmType[] = ['law_firm', 'accountant', 'agency'];

export const FIRM_TYPE_LABELS: Record<FirmType, string> = {
  law_firm: 'Law firm',
  accountant: 'Accountant',
  agency: 'Agency',
};

export const STATUS_LABELS: Record<RequestStatus, string> = {
  DETECTED: 'Detected',
  RESERVED: 'Reserved',
  CLAIMED_INHOUSE: 'Claimed in-house',
  RELEASED: 'Released',
  OPEN: 'Open',
  AWAITING_CUSTOMER: 'Awaiting customer',
  AWARDED: 'Awarded',
  COMPLETED: 'Completed',
  DISMISSED: 'Dismissed',
  EXPIRED: 'Expired',
  CANCELLED: 'Cancelled',
};

export const BID_STATUS_LABELS: Record<BidStatus, string> = {
  submitted: 'Submitted',
  withdrawn: 'Withdrawn',
  won: 'Won',
  lost: 'Not selected',
};

export const serviceLabel = (value: string | null | undefined): string =>
  (value && SERVICE_LABELS[value as ServiceType]) || value || 'n/a';

export const statusLabel = (value: string | null | undefined): string =>
  (value && STATUS_LABELS[value as RequestStatus]) || value || 'n/a';

export const toDate = (value: Timestamp | undefined): Date | null => {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'object') {
    const seconds = value.seconds ?? value._seconds;
    return typeof seconds === 'number' ? new Date(seconds * 1000) : null;
  }
  const date = new Date(value);
  return isNaN(date.getTime()) ? null : date;
};

export const formatDateTime = (value: Timestamp | undefined): string => {
  const date = toDate(value);
  if (!date) return 'n/a';
  return new Intl.DateTimeFormat('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

// "2d 5h left", "3h 20m left", or "Closed" once the deadline has passed.
export const formatCountdown = (deadline: Timestamp | undefined, now: number): string => {
  const date = toDate(deadline);
  if (!date) return 'No deadline';
  const ms = date.getTime() - now;
  if (ms <= 0) return 'Closed';
  const minutes = Math.floor(ms / 60000);
  const days = Math.floor(minutes / 1440);
  const hours = Math.floor((minutes % 1440) / 60);
  const mins = minutes % 60;
  if (days > 0) return `${days}d ${hours}h left`;
  if (hours > 0) return `${hours}h ${mins}m left`;
  return `${Math.max(mins, 1)}m left`;
};

export const isBeforeDeadline = (deadline: Timestamp | undefined, now: number): boolean => {
  const date = toDate(deadline);
  return date ? date.getTime() > now : false;
};

export const formatPrice = (priceCents: number, priceType?: string): string => {
  const amount = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(
    priceCents / 100
  );
  return priceType === 'from' ? `from ${amount}` : amount;
};

export const formatCountries = (countries: string[] | null | undefined): string =>
  countries && countries.length ? countries.map(c => c.toUpperCase()).join(', ') : 'n/a';

// "pt, br ; DE" -> ["PT", "BR", "DE"]
export const parseCodes = (value: string, upper: boolean): string[] =>
  value
    .split(/[\s,;]+/)
    .map(part => part.trim())
    .filter(Boolean)
    .map(part => (upper ? part.toUpperCase() : part.toLowerCase()));

// Current time, refreshed on an interval so countdowns tick.
export const useNow = (intervalMs = 60000): number => {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), intervalMs);
    return () => window.clearInterval(id);
  }, [intervalMs]);
  return now;
};
